/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { mobileUtils } from '@blend/consumer-components';
import { useEffect, useRef } from 'react';
import useLocalVideoToggle from '../../../../contexts/videoProvider/hooks/useLocalVideoToggle';
import { useVideoContext } from '../../../../contexts/videoProvider/videoContext';

import { VideoProviderName } from '../../../../contexts/videoProvider/types';

/**
 * This component adds a visibilitychange handler to the document when
  the user is using a mobile device. When the handler detects that
  the browser has been backgrounded, it unpublishes the users local
  video track. The browser cannot send video to the room when it has
  been backgrounded, so unpublishing the track stops video capture
  on the device, and triggers a UI update for all other participants
  to show that this user's video track has been turned off.
 */
export default function AttachVisibilityHandler() {
  const { room, videoProvider } = useVideoContext();
  const [isVideoEnabled, toggleVideoEnabled] = useLocalVideoToggle();
  const shouldRepublishVideoOnForeground = useRef(false);

  useEffect(() => {
    // TODO: (ZOOMSDK) - Add support for the visibility handler
    if (videoProvider.name === VideoProviderName.TWILIO && room && mobileUtils.isMobileDevice()) {
      const handleVisibilityChange = () => {
        // We don't need to unpublish the local video track if it has already been unpublished
        if (document.visibilityState === 'hidden' && isVideoEnabled) {
          shouldRepublishVideoOnForeground.current = true;
          toggleVideoEnabled();

          // Don't publish the local video track if it wasn't published before the app was backgrounded
        } else if (shouldRepublishVideoOnForeground.current) {
          shouldRepublishVideoOnForeground.current = false;
          toggleVideoEnabled();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
    return;
  }, [isVideoEnabled, room, toggleVideoEnabled]);

  return null;
}
